import type {
  MapCloudPoint,
  MapPointCloud,
  MapPointCloudSetOptions,
} from '@gpx-spa/geo-domain'
import L from 'leaflet'
import type { MapInteractionController } from '../shared/map-interaction-controller.js'
import { resolvePointCloudThinning } from '../shared/thin-point-cloud-by-min-gap.js'
import { TRACK_VERTEX_UNDER_MARKERS_PANE } from './leaflet-constants.js'
import type { LeafletHitRouter } from './leaflet-hit-router.js'

const DEFAULT_RADIUS = 6
const DEFAULT_BORDER = '#ffffff'
const DEFAULT_BORDER_WIDTH = 2
/** Max screen distance (px) from click to point center to count as a hit. */
const HIT_PIXEL_RADIUS = 10
/**
 * Screen grid for hover. mousemove only hit-tests points in the cursor cell and its neighbors.
 */
const HIT_CELL_PX = 48

function hitCellKey(x: number, y: number): string {
  return `${Math.floor(x / HIT_CELL_PX)},${Math.floor(y / HIT_CELL_PX)}`
}

function neighborCellKeys(x: number, y: number): string[] {
  const cx = Math.floor(x / HIT_CELL_PX)
  const cy = Math.floor(y / HIT_CELL_PX)
  const keys: string[] = []
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      keys.push(`${cx + dx},${cy + dy}`)
    }
  }
  return keys
}

export type LeafletCanvasPointCloudLayerOptions = {
  layerId: string
  getInteractionController?: () => MapInteractionController | null
  getHitRouter?: () => LeafletHitRouter | null
  /** Standalone mode (public ShipmentMap) when interaction controller is absent. */
  onPointClick?: (point: MapCloudPoint, latlng: L.LatLng, pointIndex: number) => void
  /** Fires for every hit point, including when the interaction controller owns the popup. */
  onPointHit?: (point: MapCloudPoint) => void
}

type IndexedPoint = MapCloudPoint & { index: number }

function ensureTrackVertexPane(map: L.Map): void {
  if (!map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)) {
    map.createPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    if (pane) {
      pane.style.zIndex = '655'
    }
  }
}

/** Ensures the track-vertex pane exists on a raw Leaflet map (public ShipmentMap). */
export function ensureLeafletTrackVertexPane(map: L.Map): void {
  ensureTrackVertexPane(map)
}

/**
 * Canvas batch renderer for high-count point clouds.
 * Click hit-tests the thinned set. Hover uses a screen grid: empty neighborhood skips the scan;
 * a nearby cell runs the precise distance check and sets the pointer cursor.
 * Optional thinning: stores the full set and re-thins on zoom (standalone MapLayer parity).
 */
export class LeafletCanvasPointCloudLayer extends L.Layer {
  private fullPoints: MapCloudPoint[] = []
  private points: IndexedPoint[] = []
  private thinningOptions: MapPointCloudSetOptions | undefined
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  /** Screen-cell → points whose centers fall in that cell. Rebuilt on redraw. */
  private hitBuckets = new Map<string, IndexedPoint[]>()
  private hovering = false
  private hoverFrame: number | null = null
  private pendingMove: L.LeafletMouseEvent | null = null

  private readonly testerName: string

  constructor(private readonly opts: LeafletCanvasPointCloudLayerOptions) {
    super()
    this.testerName = `point-cloud:${opts.layerId}`
  }

  /** True when the cursor is within the click slop of a visible dot. */
  hitAt(containerPoint: L.Point): boolean {
    const map = this._map
    if (!map) return false
    return this.findPointAtContainerPoint(map, containerPoint) != null
  }

  setPointCloud(cloud: MapPointCloud | null, options?: MapPointCloudSetOptions): void {
    this.fullPoints = cloud?.points ? [...cloud.points] : []
    if (options !== undefined) {
      this.thinningOptions = options
    }
    this.applyVisibleFromFull()
  }

  getPointCloud(): MapPointCloud {
    return { points: this.fullPoints }
  }

  onAdd(map: L.Map): void {
    ensureTrackVertexPane(map)
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE) ?? map.getPane('overlayPane')
    this.canvas = L.DomUtil.create(
      'canvas',
      'leaflet-canvas-point-cloud',
    ) as HTMLCanvasElement
    const canvas = this.canvas
    canvas.style.position = 'absolute'
    /**
     * Hit-testing for this layer runs through `map.on('click'/'mousemove', ...)` (pub/sub, bubbles
     * from whatever the real DOM target is), not a direct listener on this canvas — so it does not
     * need to be the pointer-events target itself. Leaving this `auto` made this full-viewport
     * canvas opaque to clicks for any sibling pane below it (e.g. `overlayPane`/`gpxVectors`
     * Leaflet Path renderers, which DO bind `click`/`mousemove` directly on their own canvas element
     * and never receive the event once a higher sibling canvas is the actual event target).
     */
    canvas.style.pointerEvents = 'none'
    canvas.style.zIndex = '655'
    pane?.appendChild(canvas)
    this.ctx = canvas.getContext('2d')
    map.on('moveend zoomend resize viewreset', this.onViewChanged, this)
    map.on('click', this.onMapClick, this)
    map.on('mousemove', this.onMapMouseMove, this)
    this.opts.getHitRouter?.()?.register({
      name: this.testerName,
      priority: 100,
      hitAt: (point) => this.hitAt(point),
    })
    this.applyVisibleFromFull()
  }

  onRemove(map: L.Map): void {
    this.clearHover()
    if (this.hoverFrame != null) {
      cancelAnimationFrame(this.hoverFrame)
      this.hoverFrame = null
    }
    this.pendingMove = null
    this.opts.getHitRouter?.()?.unregister(this.testerName)
    map.off('moveend zoomend resize viewreset', this.onViewChanged, this)
    map.off('click', this.onMapClick, this)
    map.off('mousemove', this.onMapMouseMove, this)
    if (this.canvas?.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas)
    }
    this.canvas = null
    this.ctx = null
  }

  private applyVisibleFromFull(): void {
    const map = this._map
    const zoom = map?.getZoom() ?? 15
    const visible = resolvePointCloudThinning(
      this.fullPoints,
      zoom,
      this.thinningOptions,
    )
    this.points = visible.map((p, index) => ({ ...p, index }))
    this.rebuildHitBuckets()
    this.redraw()
  }

  /** Screen cells for hover/click. Independent of the 2d context so hits work if paint is skipped. */
  private rebuildHitBuckets(): void {
    const map = this._map
    const buckets = new Map<string, IndexedPoint[]>()
    if (map) {
      for (const p of this.points) {
        const pt = map.latLngToContainerPoint(L.latLng(p.position.lat, p.position.lng))
        const key = hitCellKey(pt.x, pt.y)
        const bucket = buckets.get(key)
        if (bucket) bucket.push(p)
        else buckets.set(key, [p])
      }
    }
    this.hitBuckets = buckets
  }

  private onViewChanged = (): void => {
    this.applyVisibleFromFull()
  }

  private redraw = (): void => {
    const map = this._map
    const canvas = this.canvas
    const ctx = this.ctx
    if (!map || !canvas || !ctx) return

    const size = map.getSize()
    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1
    canvas.width = Math.floor(size.x * dpr)
    canvas.height = Math.floor(size.y * dpr)
    canvas.style.width = `${size.x}px`
    canvas.style.height = `${size.y}px`

    const topLeft = map.containerPointToLayerPoint([0, 0])
    L.DomUtil.setPosition(canvas, topLeft)

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size.x, size.y)

    for (const p of this.points) {
      const pt = map.latLngToContainerPoint(
        L.latLng(p.position.lat, p.position.lng),
      )
      const r = p.style.radius ?? DEFAULT_RADIUS
      const border = p.style.borderColor ?? DEFAULT_BORDER
      const bw = p.style.borderWidth ?? DEFAULT_BORDER_WIDTH
      ctx.beginPath()
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2)
      ctx.fillStyle = p.style.fillColor
      ctx.fill()
      ctx.strokeStyle = border
      ctx.lineWidth = bw
      ctx.stroke()
    }
  }

  private candidatesNear(containerPoint: L.Point): IndexedPoint[] {
    const out: IndexedPoint[] = []
    for (const key of neighborCellKeys(containerPoint.x, containerPoint.y)) {
      const bucket = this.hitBuckets.get(key)
      if (bucket) out.push(...bucket)
    }
    return out
  }

  private findPointAtContainerPoint(
    map: L.Map,
    containerPoint: L.Point,
  ): IndexedPoint | null {
    const candidates = this.candidatesNear(containerPoint)
    if (candidates.length === 0) return null
    let best: IndexedPoint | null = null
    let bestDist = HIT_PIXEL_RADIUS
    for (const p of candidates) {
      const pt = map.latLngToContainerPoint(
        L.latLng(p.position.lat, p.position.lng),
      )
      const dx = pt.x - containerPoint.x
      const dy = pt.y - containerPoint.y
      const dist = Math.hypot(dx, dy)
      const hitSlop = (p.style.radius ?? DEFAULT_RADIUS) + 4
      if (dist <= hitSlop && dist < bestDist) {
        bestDist = dist
        best = p
      }
    }
    return best
  }

  private onMapClick = (e: L.LeafletMouseEvent): void => {
    const map = this._map
    if (!map) return
    const point = this.findPointAtContainerPoint(map, e.containerPoint)
    if (!point) return

    L.DomEvent.stopPropagation(e)
    this.opts.onPointHit?.(point)

    const controller = this.opts.getInteractionController?.()
    if (controller?.hasInteractions()) {
      void controller.dispatchHit({
        trigger: 'click',
        latlng: { lat: point.position.lat, lng: point.position.lng },
        nativeEvent: e,
        target: {
          type: 'pointCloud',
          layerId: this.opts.layerId,
          position: { lat: point.position.lat, lng: point.position.lng },
          point,
          pointIndex: point.index,
        },
      })
      return
    }

    this.opts.onPointClick?.(point, e.latlng, point.index)
  }

  private onMapMouseMove = (e: L.LeafletMouseEvent): void => {
    this.pendingMove = e
    if (this.hoverFrame != null) return
    this.hoverFrame = requestAnimationFrame(() => {
      this.hoverFrame = null
      const ev = this.pendingMove
      this.pendingMove = null
      if (ev) this.applyHover(ev)
    })
  }

  private applyHover(e: L.LeafletMouseEvent): void {
    const map = this._map
    if (!map) return
    if (map.dragging?.moving()) return
    const point = this.findPointAtContainerPoint(map, e.containerPoint)
    const router = this.opts.getHitRouter?.()
    if (point) {
      if (this.hovering) return
      this.hovering = true
      if (router) router.setHover(this.testerName, true)
      else map.getContainer().style.cursor = 'pointer'
      this.opts.getInteractionController?.()?.pointerEnter()
      return
    }
    this.clearHover()
  }

  private clearHover(): void {
    if (!this.hovering) return
    this.hovering = false
    const router = this.opts.getHitRouter?.()
    if (router) router.setHover(this.testerName, false)
    else if (this._map) this._map.getContainer().style.cursor = ''
    this.opts.getInteractionController?.()?.scheduleHoverLeave()
  }
}

export function createLeafletCanvasPointCloudLayer(
  options: LeafletCanvasPointCloudLayerOptions,
): LeafletCanvasPointCloudLayer {
  return new LeafletCanvasPointCloudLayer(options)
}
